import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";

import {
  type AuthProviderConfig,
  type AuthProviderId,
  type B2cConfig,
  type EntraConfig,
  clearSelectedProvider,
  getSelectedProvider,
  loadEnabledProviders,
  setSelectedProvider,
} from "./authProviders";
import {
  clearStoredTokens,
  getFreshAccessToken,
  getStoredTokens,
  readB2cAccountLabel,
  ropcLogin,
} from "./b2cClient";
import { jwtExpiryMs, jwtUserOid } from "./jwt";
import { setAuthenticatedUser } from "../telemetry/appInsights";

// Refresh the access token this many ms before its `exp` claim. Covers
// clock skew and keeps requests from racing an expiry that lands mid-flight.
const TOKEN_REFRESH_LEAD_MS = 60_000;

/**
 * Unified auth state across all three providers. Consumers read `accessToken` /
 * `isAuthenticated` / `isLoading` without caring which provider is active;
 * Apollo reads the live value via `getToken()`.
 *
 * Login methods are separated by shape — Entra is interactive (MSAL redirect),
 * while the two B2C providers take email/password credentials for ROPC.
 */
export interface AuthState {
  enabledProviders: AuthProviderConfig[];
  selectedProvider: AuthProviderId | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  accountLabel: string | null;
  loginWithEntra: () => Promise<void>;
  loginWithB2c: (
    providerId: "pathways-b2c" | "provider-portal-b2c",
    email: string,
    password: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  /** Stable getter — for Apollo authLink, which runs outside React. */
  getToken: () => string | null;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuthContext(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuthContext must be inside <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const enabledProviders = useMemo(() => loadEnabledProviders(), []);
  const entraCfg = enabledProviders.find(
    (p): p is EntraConfig => p.id === "entra",
  );
  const pathwaysCfg = enabledProviders.find(
    (p): p is B2cConfig => p.id === "pathways-b2c",
  );
  const providerPortalCfg = enabledProviders.find(
    (p): p is B2cConfig => p.id === "provider-portal-b2c",
  );

  // useMsal is guaranteed to be mounted — App wires the providers.
  const msal = useMsal();

  const [selectedProvider, setSelectedProviderState] =
    useState<AuthProviderId | null>(() => getSelectedProvider());
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoadingExplicit, setIsLoadingExplicit] = useState(false);

  const tokenRef = useRef<string | null>(null);
  tokenRef.current = accessToken;

  const b2cCfgFor = useCallback(
    (id: "pathways-b2c" | "provider-portal-b2c") =>
      id === "pathways-b2c" ? pathwaysCfg : providerPortalCfg,
    [pathwaysCfg, providerPortalCfg],
  );

  /**
   * Pull a fresh token for the currently selected provider. Called on mount,
   * when the selection changes, and when MSAL's account list settles after a
   * popup login.
   */
  const refreshToken = useCallback(async () => {
    if (!selectedProvider) {
      setAccessToken(null);
      return;
    }

    if (selectedProvider === "entra") {
      if (!entraCfg || !msal) {
        setAccessToken(null);
        return;
      }
      const account = msal.accounts[0] ?? null;
      if (!account) {
        setAccessToken(null);
        return;
      }
      try {
        const res = await msal.instance.acquireTokenSilent({
          account,
          scopes: entraCfg.apiScopes,
        });
        setAccessToken(res.accessToken);
      } catch {
        setAccessToken(null);
      }
      return;
    }

    const cfg = b2cCfgFor(selectedProvider);
    if (!cfg) {
      setAccessToken(null);
      return;
    }
    setAccessToken(await getFreshAccessToken(cfg));
  }, [selectedProvider, entraCfg, msal, b2cCfgFor]);

  useEffect(() => {
    void refreshToken();
  }, [refreshToken]);

  // Schedule a refresh `TOKEN_REFRESH_LEAD_MS` before the current token
  // expires. If the token is already past (or near) its `exp` when we see
  // it — e.g. resumed from sleep — the timeout fires immediately. This keeps
  // care-management-gql from ever verifying a request with an expired
  // bearer, as long as the silent-refresh path still works.
  useEffect(() => {
    if (!accessToken) return;
    const expMs = jwtExpiryMs(accessToken);
    if (expMs === null) return;
    const delay = Math.max(0, expMs - Date.now() - TOKEN_REFRESH_LEAD_MS);
    const timer = window.setTimeout(() => {
      void refreshToken();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [accessToken, refreshToken]);

  // Sync App Insights authenticated user context whenever the access token
  // changes. Extracts the opaque OID/sub claim — never PHI. On logout
  // (accessToken → null) clears the context so future anonymous events are
  // not attributed to the previous user.
  useEffect(() => {
    setAuthenticatedUser(jwtUserOid(accessToken) ?? undefined);
  }, [accessToken]);

  // We track explicit login/logout transitions.
  const isLoading = isLoadingExplicit;

  const loginWithEntra = useCallback(async () => {
    if (!entraCfg) throw new Error("Entra is not an enabled provider");

    if (!msal) throw new Error("MSAL not initialised");
    // Full-page redirect rather than a pop-up. Pop-ups are unreliable on
    // mobile browsers, and because our redirectUri is the SPA itself, the
    // pop-up booted the whole app and the router (`/` → `/members`) stripped
    // MSAL's response hash before it could be read — surfacing as
    // hash_empty_error (most often on mobile). The redirect flow completes on
    // the next boot: main.tsx runs handleRedirectPromise() *before* React
    // mounts, so the hash is consumed before the router can touch it, and the
    // active provider is recorded there.
    setIsLoadingExplicit(true);
    try {
      await msal.instance.loginRedirect({ scopes: entraCfg.apiScopes });
      // loginRedirect navigates away; code below does not run on success.
    } catch (e) {
      // Redirect failed to even start (e.g. interaction_in_progress) — clear
      // the pending state so LoginPage can surface a friendly error.
      setIsLoadingExplicit(false);
      throw e;
    }
  }, [entraCfg, msal]);

  const loginWithB2c = useCallback(
    async (
      id: "pathways-b2c" | "provider-portal-b2c",
      email: string,
      password: string,
    ) => {
      const cfg = b2cCfgFor(id);
      if (!cfg) throw new Error(`Provider ${id} is not enabled`);
      setIsLoadingExplicit(true);
      try {
        const tokens = await ropcLogin(cfg, email, password);
        setSelectedProvider(id);
        setSelectedProviderState(id);
        setAccessToken(tokens.access_token);
      } finally {
        setIsLoadingExplicit(false);
      }
    },
    [b2cCfgFor],
  );

  const logout = useCallback(async () => {
    const wasProvider = selectedProvider;
    clearSelectedProvider();
    setSelectedProviderState(null);
    setAccessToken(null);

    if (wasProvider === "pathways-b2c" || wasProvider === "provider-portal-b2c") {
      clearStoredTokens(wasProvider);
      return;
    }

    if (wasProvider === "entra") {
      if (msal) {
        const account = msal.accounts[0] ?? null;
        try {
          // Full-page redirect to pair with loginRedirect. logoutPopup is
          // blocked by popup-blockers (notably Safari); postLogoutRedirectUri
          // is configured in msalConfig.ts.
          await msal.instance.logoutRedirect({ account });
        } catch {
          /* ignore — redirect failed to start */
        }
      }
    }
  }, [msal, selectedProvider]);

  const accountLabel = useMemo(() => {
    if (!selectedProvider) return null;
    if (selectedProvider === "entra") {
      return msal?.accounts[0]?.username ?? null;
    }
    // B2C — whichever provider is active. `readB2cAccountLabel` prefers the
    // email the session was AUTHENTICATED with over anything decoded out of the
    // id_token; see its doc comment for why that order is load-bearing
    // (AM-5930: the Provider Portal policy emits no email claim, so decoding
    // first surfaced an opaque Key ID as the label).
    return readB2cAccountLabel(getStoredTokens(selectedProvider));
  }, [msal?.accounts, selectedProvider]);

  const value: AuthState = {
    enabledProviders,
    selectedProvider,
    isAuthenticated: !!accessToken,
    isLoading,
    accessToken,
    accountLabel,
    loginWithEntra,
    loginWithB2c,
    logout,
    getToken: () => tokenRef.current,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

